import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { metaApi, segmentsApi } from "@/api";
import type { SegmentFilter } from "@/types";
import { ArrowLeft, Save } from "lucide-react";

const traderTypeOptions = [
  "業販・取付", "業販", "一次代理店", "代理店", "ディーラー",
  "取付", "二次代理店", "販社", "0次代理店", "三次代理店",
];
const sourceOptions = [
  { value: "TRADER", label: "取引先" },
  { value: "TRADER_CONTACT", label: "担当者" },
  { value: "MEMBER", label: "会員" },
];
const rfmRankOptions = ["A", "B", "C", "D"];

function CheckboxGroup({
  label,
  options,
  selected,
  onChange,
}: {
  label: string;
  options: { value: string; label: string }[];
  selected: string[];
  onChange: (vals: string[]) => void;
}) {
  const toggle = (val: string) => {
    onChange(
      selected.includes(val)
        ? selected.filter((v) => v !== val)
        : [...selected, val],
    );
  };

  return (
    <div>
      <label className="block text-xs font-medium text-slate-600 mb-1">{label}</label>
      <div className="flex flex-wrap gap-2">
        {options.map((opt) => (
          <button
            key={opt.value}
            type="button"
            onClick={() => toggle(opt.value)}
            className={`px-2 py-1 rounded text-xs border transition-colors ${
              selected.includes(opt.value)
                ? "bg-blue-500 text-white border-blue-500"
                : "bg-white text-slate-600 border-slate-300 hover:border-blue-300"
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function MetaAudienceEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isNew = !id;

  const [name, setName] = useState("");
  const [traderType, setTraderType] = useState<string[]>([]);
  const [source, setSource] = useState<string[]>([]);
  const [rfmRank, setRfmRank] = useState<string[]>([]);
  const [previewCount, setPreviewCount] = useState<number | null>(null);

  const { data: existing } = useQuery({
    queryKey: ["meta-audience", id],
    queryFn: () => metaApi.getAudience(id!),
    enabled: !!id,
  });

  useEffect(() => {
    if (existing) {
      setName(existing.name);
      const sq = existing.segmentQuery as SegmentFilter | null;
      if (sq) {
        setTraderType(sq.traderType || []);
        setSource(sq.source || []);
        setRfmRank(sq.rfmRank || []);
      }
    }
  }, [existing]);

  const buildFilterParams = useCallback((): Record<string, string> => {
    const params: Record<string, string> = {};
    if (traderType.length) params.traderType = traderType.join(",");
    if (source.length) params.source = source.join(",");
    if (rfmRank.length) params.rfmRank = rfmRank.join(",");
    return params;
  }, [traderType, source, rfmRank]);

  useEffect(() => {
    const params = buildFilterParams();
    segmentsApi.preview(params).then((r) => setPreviewCount(r.count)).catch(() => {});
  }, [buildFilterParams]);

  const buildSegmentQuery = (): SegmentFilter => {
    const filter: SegmentFilter = {};
    if (traderType.length) filter.traderType = traderType;
    if (source.length) filter.source = source;
    if (rfmRank.length) filter.rfmRank = rfmRank;
    return filter;
  };

  const saveMutation = useMutation({
    mutationFn: () => {
      const data = {
        name,
        segmentQuery: buildSegmentQuery(),
      };
      if (isNew) return metaApi.createAudience(data);
      return metaApi.updateAudience(id!, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["meta-audiences"] });
      navigate("/meta-audiences");
    },
  });

  const canSave = name.trim().length > 0;

  return (
    <div className="p-6 max-w-3xl mx-auto">
      <button
        onClick={() => navigate("/meta-audiences")}
        className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 mb-4"
      >
        <ArrowLeft size={16} />
        オーディエンス一覧に戻る
      </button>

      <h1 className="text-2xl font-bold mb-6">
        {isNew ? "新規オーディエンス" : "オーディエンス編集"}
      </h1>

      <div className="space-y-6">
        {/* Name */}
        <div className="bg-white p-4 rounded-lg shadow-sm border">
          <label className="block text-sm font-medium mb-2">オーディエンス名</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="例: 高ランク取引先"
            className="w-full px-3 py-2 border rounded-md text-sm"
          />
        </div>

        {/* Segment Filter */}
        <div className="bg-white p-4 rounded-lg shadow-sm border space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-bold">セグメント条件</h2>
            {previewCount !== null && (
              <span className="text-sm font-medium text-blue-600">
                対象: {previewCount.toLocaleString()} 件
              </span>
            )}
          </div>

          <CheckboxGroup
            label="取引先タイプ"
            options={traderTypeOptions.map((t) => ({ value: t, label: t }))}
            selected={traderType}
            onChange={setTraderType}
          />

          <CheckboxGroup
            label="ソース"
            options={sourceOptions}
            selected={source}
            onChange={setSource}
          />

          <CheckboxGroup
            label="RFMランク"
            options={rfmRankOptions.map((r) => ({ value: r, label: r }))}
            selected={rfmRank}
            onChange={setRfmRank}
          />
        </div>

        {/* Sync Log (edit mode only) */}
        {existing?.syncLogs && existing.syncLogs.length > 0 && (
          <div className="bg-white p-4 rounded-lg shadow-sm border">
            <h2 className="text-sm font-bold mb-3">同期ログ</h2>
            <div className="space-y-2">
              {existing.syncLogs.map((log) => (
                <div key={log.id} className="flex items-center justify-between text-xs text-slate-600 border-b pb-2 last:border-0">
                  <span>{new Date(log.createdAt).toLocaleString("ja-JP")}</span>
                  <span>+{log.added} / -{log.removed} (計 {log.totalMatched})</span>
                  {log.error && <span className="text-red-500">{log.error}</span>}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Save */}
        <div className="flex justify-end">
          <button
            onClick={() => saveMutation.mutate()}
            disabled={!canSave || saveMutation.isPending}
            className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-md text-sm hover:bg-blue-600 disabled:opacity-50"
          >
            <Save size={16} />
            {saveMutation.isPending ? "保存中..." : "保存"}
          </button>
        </div>
      </div>
    </div>
  );
}
